/**
 * How the operator console renders a customer's contact details, and the one rule that
 * governs all of it.
 *
 * ## Operator screens are the exception, and only the exception
 *
 * The app masks a phone number everywhere a customer might see one. This console is the
 * deliberate exception: every action here is already behind an admin token and an
 * `audit_log` row, and support answering a ticket cannot dial a number with three digits
 * missing. So wherever the API supplies a full `phone`, the console shows the full `phone`.
 *
 * ## What this file will never do
 *
 * It will never turn a mask back into a number. `+234 807 ••• 4291` stays exactly that.
 * There is no unmasking helper here and there must never be one: the digits are not in the
 * response, so anything that produced them would be inventing them. What this file does is
 * *prefer* the full value when the API sends one, fall back to the mask when it does not,
 * and — the part that stops the console from lying — say out loud which of the two is on
 * screen. A mask presented as if it were the whole number is the specific failure this
 * exists to prevent: an operator reads it down a phone line, the call does not connect, and
 * nothing on the screen ever told them why.
 */

/** Which of the three things is actually on screen. */
export type PhoneSource = 'full' | 'masked' | 'none';

export interface PhoneView {
  /** The characters to render. Never assembled, always straight from the API. */
  readonly value: string;
  readonly source: PhoneSource;
  /** What `CopyButton` should put on the clipboard, or null when there is nothing to copy. */
  readonly copyValue: string | null;
  /** The copy button's accessible label, naming what it actually copies. */
  readonly copyLabel: string;
  /** The short sentence shown beside a masked value. Null when the value is whole. */
  readonly note: string | null;
}

/**
 * Anything the API can hand us that carries a phone: a user row, a user detail, a
 * transaction row, a support thread. `phone` is the provisional full field (see
 * `contracts/pending.ts`); `maskedPhone` is what the contract publishes today.
 */
export interface PhoneBearing {
  readonly phone?: string | null;
  readonly maskedPhone?: string | null;
}

const NOT_SUPPLIED =
  'The API has not sent the full number for this record yet, so this is the masked one.';

function usable(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The full number when there is one, the mask when there is not, and a plain sentence
 * saying which.
 *
 * The full value wins even when it is byte-identical to the mask, because that is a server
 * that genuinely sent a full number which happens to look masked, and second-guessing it
 * here would mean the console holding an opinion about the API's own field.
 */
export function phoneView(row: PhoneBearing): PhoneView {
  const full = usable(row.phone);
  if (full !== null) {
    return {
      value: full,
      source: 'full',
      copyValue: full,
      copyLabel: 'phone number',
      note: null,
    };
  }
  const masked = usable(row.maskedPhone);
  if (masked !== null) {
    return {
      value: masked,
      source: 'masked',
      copyValue: masked,
      copyLabel: 'masked phone number',
      note: NOT_SUPPLIED,
    };
  }
  return {
    value: 'No phone on file',
    source: 'none',
    copyValue: null,
    copyLabel: 'phone number',
    note: null,
  };
}

/**
 * The letter on an avatar disc when there is no photo.
 *
 * Never a question mark, and never an empty circle. The candidates are tried in the order
 * an operator would recognise the customer by — their name first, then a username, then an
 * email, and finally the account id, which always yields *something* because a uuid always
 * has a leading hex digit. Non-alphanumerics are skipped rather than shown, so a display
 * name of `"@ada"` gives `A` instead of `@`.
 *
 * Iterated by code point (`[...value]`) rather than by index, because a name that starts
 * with an emoji or a surrogate pair would otherwise be sliced in half and rendered as a
 * replacement glyph, which is the broken-image failure this whole component avoids, moved
 * one layer down.
 */
export function initialFor(...candidates: readonly (string | null | undefined)[]): string {
  for (const candidate of candidates) {
    const value = usable(candidate);
    if (value === null) continue;
    for (const char of value) {
      if (/[\p{L}\p{N}]/u.test(char)) return char.toUpperCase();
    }
  }
  return 'GX';
}

/** How many disc tones `app.css` defines. Kept here so the two cannot disagree silently. */
export const AVATAR_TONES = 6;

/**
 * Which tone a customer's disc gets, derived from a stable seed (their id).
 *
 * Deterministic on purpose: the same customer is the same colour on the users table, on
 * their detail page and in a support thread, so an operator with three tabs open can tell
 * at a glance that they are looking at one person. A random or index-based colour would
 * repaint on every sort and teach the opposite.
 */
export function avatarTone(seed: string | null | undefined): number {
  const value = usable(seed);
  if (value === null) return 1;
  let hash = 0;
  for (const char of value) {
    hash = (hash * 31 + char.codePointAt(0)!) % 100_000_007;
  }
  return (hash % AVATAR_TONES) + 1;
}
